import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, FileText, Package, HelpCircle, MessageSquare,
  Image as ImageIcon, Download, LogOut, Quote, ShieldCheck,
  Sparkles, BarChart3, Settings as SettingsIcon, Smartphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/admin/leads", icon: Sparkles, label: "Leads" },
  { to: "/admin/contacts", icon: MessageSquare, label: "Contacts" },
  { to: "/admin/blog", icon: FileText, label: "Blog" },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQs" },
  { to: "/admin/testimonials", icon: Quote, label: "Testimonials" },
  { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
  { to: "/admin/downloads", icon: Download, label: "Downloads" },
  { to: "/admin/apk", icon: Smartphone, label: "APK Manager" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    toast.success("Signed out.");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100" data-testid="admin-layout">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-screen">
        <aside className="border-r border-white/5 bg-[#0B1221] hidden lg:flex flex-col">
          <Link to="/" className="px-6 py-5 flex items-center gap-2.5 border-b border-white/5">
            <div className="relative w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-sky-500 to-[#0A1128]" />
              <span className="relative font-bold text-white text-xs">M</span>
              <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#D4AF37] ring-2 ring-[#0B1221]" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold font-[Outfit]">MFB Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-sky-400/80">Control Center</div>
            </div>
          </Link>
          <nav className="flex-1 px-3 py-5 space-y-0.5">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive ? "bg-sky-500/10 text-white border border-sky-500/20" : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="w-8 h-8 rounded-full bg-sky-500/20 ring-1 ring-white/10 flex items-center justify-center text-xs font-semibold">
                {user?.email?.[0]?.toUpperCase()}
              </div>
              <div className="text-xs flex-1 leading-tight">
                <div className="text-white font-medium truncate">{user?.name || "Admin"}</div>
                <div className="text-slate-500 truncate">{user?.email}</div>
              </div>
              <button onClick={onLogout} data-testid="admin-logout-btn" className="text-slate-400 hover:text-rose-400">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        <div className="flex flex-col">
          <header className="lg:hidden border-b border-white/5 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> MFB Admin
            </div>
            <button onClick={onLogout} className="text-slate-400" data-testid="admin-mobile-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </header>
          <main className="flex-1 p-6 lg:p-10">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
